var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __asyncValues = (this && this.__asyncValues) || function (o) {
    if (!Symbol.asyncIterator) throw new TypeError("Symbol.asyncIterator is not defined.");
    var m = o[Symbol.asyncIterator], i;
    return m ? m.call(o) : (o = typeof __values === "function" ? __values(o) : o[Symbol.iterator](), i = {}, verb("next"), verb("throw"), verb("return"), i[Symbol.asyncIterator] = function () { return this; }, i);
    function verb(n) { i[n] = o[n] && function (v) { return new Promise(function (resolve, reject) { v = o[n](v), settle(resolve, reject, v.done, v.value); }); }; }
    function settle(resolve, reject, d, v) { Promise.resolve(v).then(function(v) { resolve({ value: v, done: d }); }, reject); }
};
var _a;
import { connect, JSONCodec, jwtAuthenticator, Events, } from 'nats.ws';
import { createUser, fromSeed } from 'nkeys.js';
import { store } from './store';
import { updateMessage } from './natsslice';
import axios from 'axios';
import { CacheService } from '../common/util/cacheservice';
import { DataService } from '../app/configure/service/deviceservice';
import { Constants } from '../app/configure/models/constants';
import { sendAlertToServer } from '../app/workspaces/floor/util';
const app_module = 'signage-module';
const LOCAL_STORAGE_PRIVATE_KEY = 'nats_private_key';
const LOCAL_STORAGE_PUBLIC_KEY = 'nats_public_key';
const LOCAL_STORAGE_IS_REGISTERED = 'nats_is_registered';
const cacheService = CacheService.getInstance(app_module);
function debugLog(...args) {
    if (window.enableDebugLogs)
        console.log(...args);
}
function debugError(...args) {
    console.error(...args);
}
window.enableDebugLogs = (_a = window.enableDebugLogs) !== null && _a !== void 0 ? _a : false;
function dbgLog(...args) {
    if (window.enableDebugLogs)
        console.log(...args);
}
function dbgErr(...args) {
    if (window.enableDebugLogs)
        console.error(...args);
}
// Singleton NATS Client
class NatsClient {
    constructor() {
        this.nc = null;
        this.nkeyPair = null;
        this.jwtToken = null;
        this.jetStreamClient = null;
        this.codec = JSONCodec();
        this.streamName = null;
        this.lastReceivedSequence = new Map();
        this.currentSubscriptions = new Map();
        this.globalSubscriptions = new Map();
        // Health check and inactivity monitoring properties
        this.lastMessageTime = new Map(); // Track last message time per subject
        this.healthCheckInterval = null;
        this.inactivityTimeout = 5 * 60 * 1000; // 5 minutes default inactivity timeout
        this.healthCheckFrequency = 30 * 1000; // 30 seconds health check frequency
        this.connectionParams = null;
        this.isHealthCheckEnabled = true;
        this.consecutiveHealthFailures = 0;
        this.maxConsecutiveFailures = 3;
        this.connectionId = 0;
        this.connectRetryTimeout = null;
        // Connect to NATS WebSocket server
        this.isConnecting = false;
        this.isConnected = false;
    }
    // Get singleton instance
    static getInstance() {
        if (!NatsClient.instance) {
            NatsClient.instance = new NatsClient();
        }
        return NatsClient.instance;
    }
    // Health check and monitoring methods
    setInactivityTimeout(timeout) {
        this.inactivityTimeout = timeout;
    }
    setHealthCheckFrequency(frequency) {
        this.healthCheckFrequency = frequency;
        if (this.healthCheckInterval) {
            clearInterval(this.healthCheckInterval);
            this.startHealthCheckMonitoring();
        }
    }
    enableHealthCheck(enabled) {
        this.isHealthCheckEnabled = enabled;
        if (enabled && !this.healthCheckInterval) {
            this.startHealthCheckMonitoring();
        }
        else if (!enabled && this.healthCheckInterval) {
            clearInterval(this.healthCheckInterval);
            this.healthCheckInterval = null;
        }
    }
    updateLastMessageTime(subject) {
        this.lastMessageTime.set(subject, Date.now());
    }
    isSubjectInactive(subject) {
        const lastTime = this.lastMessageTime.get(subject);
        if (!lastTime)
            return true; // No messages received yet
        return Date.now() - lastTime > this.inactivityTimeout;
    }
    performConnectionHealthCheck() {
        return __awaiter(this, void 0, void 0, function* () {
            const logPrefix = 'NatsClient :: performConnectionHealthCheck ::';
            if (!this.nc || this.nc.isClosed()) {
                dbgErr(logPrefix, 'NATS connection is closed or null');
                return false;
            }
            try {
                // Check core NATS connection health using flush
                yield this.nc.flush();
                dbgLog(logPrefix, 'Connection health check passed using flush');
                // Check if we can access NATS connection stats/info
                const stats = this.nc.stats();
                if (stats) {
                    dbgLog(logPrefix, 'NATS connection stats:', {
                        inMsgs: stats.inMsgs,
                        outMsgs: stats.outMsgs,
                        inBytes: stats.inBytes,
                        outBytes: stats.outBytes,
                    });
                }
                // If we have subscribed subjects, test DirectGet functionality as secondary check
                const allSubjects = new Set([
                    ...this.globalSubscriptions.keys(),
                    ...this.currentSubscriptions.keys(),
                ]);
                if (allSubjects.size > 0 && this.streamName) {
                    // Pick the first available subject and try to get its latest message
                    const testSubject = Array.from(allSubjects)[0];
                    const directGetSubject = `$JS.API.DIRECT.GET.${this.streamName}.${testSubject}`;
                    yield this.nc.request(directGetSubject, '', { timeout: 5000 });
                    dbgLog(logPrefix, 'Connection health check passed using subscribed subject:', testSubject);
                }
                return true;
            }
            catch (error) {
                dbgErr(logPrefix, 'Connection health check failed:', error);
                return false;
            }
        });
    }
    performSubscriptionHealthCheck() {
        return __awaiter(this, void 0, void 0, function* () {
            const logPrefix = 'NatsClient :: performSubscriptionHealthCheck ::';
            // Only check if core NATS subscriptions are still active/open
            // Don't worry about message activity - some subjects may legitimately not have updates
            const closedSubscriptions = [];
            // Check global subscriptions
            this.globalSubscriptions.forEach((subscription, subject) => {
                if (subscription && subscription.isClosed()) {
                    closedSubscriptions.push(`global:${subject}`);
                }
            });
            // Check current subscriptions
            this.currentSubscriptions.forEach((subscription, subject) => {
                if (subscription && subscription.isClosed()) {
                    closedSubscriptions.push(`current:${subject}`);
                }
            });
            if (closedSubscriptions.length > 0) {
                dbgErr(logPrefix, 'Closed subscriptions detected:', closedSubscriptions);
                return false;
            }
            // All subscriptions are open - this is healthy
            dbgLog(logPrefix, 'All subscriptions are active and open');
            return true;
        });
    }
    performDirectGetHealthCheck() {
        return __awaiter(this, void 0, void 0, function* () {
            const logPrefix = 'NatsClient :: performDirectGetHealthCheck ::';
            if (!this.nc || !this.streamName) {
                return false;
            }
            try {
                // Use one of the subscribed subjects for DirectGet health check if available
                const allSubjects = new Set([
                    ...this.globalSubscriptions.keys(),
                    ...this.currentSubscriptions.keys(),
                ]);
                if (allSubjects.size > 0) {
                    // Pick the first available subject and try DirectGet
                    const testSubject = Array.from(allSubjects)[0];
                    const directGetSubject = `$JS.API.DIRECT.GET.${this.streamName}.${testSubject}`;
                    yield this.nc.request(directGetSubject, '', { timeout: 5000 });
                    dbgLog(logPrefix, 'DirectGet health check passed using subscribed subject:', testSubject);
                    return true;
                }
                // If no subjects are subscribed, we can't perform DirectGet health check
                // This is not necessarily a failure - just means no subjects to test
                dbgLog(logPrefix, 'No subscribed subjects available for DirectGet health check');
                return true;
            }
            catch (error) {
                dbgErr(logPrefix, 'DirectGet health check failed:', error);
                return false;
            }
        });
    }
    // Clear the per-subject JetStream sequence tracking map.
    // Must be called on every disconnect/reconnect so that a migrated NATS
    // cluster (whose stream sequences restart from low values) is not blocked
    // by stale, higher sequence numbers from the previous cluster.
    clearLastReceivedSequence() {
        this.lastReceivedSequence.clear();
        dbgLog('NatsClient :: clearLastReceivedSequence :: cleared sequence tracking map');
    }
    // Get current subscription state
    getCurrentSubscriptionState() {
        return {
            globalSubjects: Array.from(this.globalSubscriptions.keys()),
            currentSubjects: Array.from(this.currentSubscriptions.keys()),
        };
    }
    // Get closed subscriptions
    getClosedSubscriptions() {
        const closedGlobalSubjects = [];
        const closedCurrentSubjects = [];
        this.globalSubscriptions.forEach((subscription, subject) => {
            if (subscription && subscription.isClosed()) {
                closedGlobalSubjects.push(subject);
            }
        });
        this.currentSubscriptions.forEach((subscription, subject) => {
            if (subscription && subscription.isClosed()) {
                closedCurrentSubjects.push(subject);
            }
        });
        return {
            globalSubjects: closedGlobalSubjects,
            currentSubjects: closedCurrentSubjects,
        };
    }
    // Common method to establish/reestablish subscriptions
    establishSubscriptions(subscriptionState, withDirectGet = true, logContext = 'establishSubscriptions') {
        return __awaiter(this, void 0, void 0, function* () {
            const logPrefix = `NatsClient :: ${logContext} ::`;
            if (!this.nc) {
                dbgErr(logPrefix, 'NATS connection not available');
                return;
            }
            // Establish global subscriptions
            for (const subject of subscriptionState.globalSubjects) {
                dbgLog(logPrefix, `Establishing global subscription: ${subject}`);
                const newSub = this.startSubscriber(this.nc, subject);
                if (newSub) {
                    this.globalSubscriptions.set(subject, newSub);
                    this.updateLastMessageTime(subject);
                    if (withDirectGet) {
                        yield this.getLatestMessagesForSubjectFilter(this.nc, this.streamName, subject);
                    }
                }
            }
            // Establish current subscriptions
            for (const subject of subscriptionState.currentSubjects) {
                dbgLog(logPrefix, `Establishing current subscription: ${subject}`);
                const newSub = this.startSubscriber(this.nc, subject);
                if (newSub) {
                    this.currentSubscriptions.set(subject, newSub);
                    this.updateLastMessageTime(subject);
                    if (withDirectGet) {
                        yield this.getLatestMessagesForSubjectFilter(this.nc, this.streamName, subject);
                    }
                }
            }
            const totalEstablished = subscriptionState.globalSubjects.length +
                subscriptionState.currentSubjects.length;
            if (totalEstablished > 0) {
                dbgLog(logPrefix, `Established ${subscriptionState.globalSubjects.length} global and ${subscriptionState.currentSubjects.length} current subscriptions`);
            }
        });
    }
    // Common method to perform direct gets for current subscriptions
    performDirectGetsForSubscriptions(logContext = 'performDirectGets') {
        return __awaiter(this, void 0, void 0, function* () {
            const logPrefix = `NatsClient :: ${logContext} ::`;
            if (!this.nc || !this.streamName) {
                dbgErr(logPrefix, 'NATS connection or stream name not available');
                return;
            }
            const promises = [];
            // Perform direct gets for global subscriptions
            this.globalSubscriptions.forEach((sub, filterSubject) => {
                promises.push(this.getLatestMessagesForSubjectFilter(this.nc, this.streamName, filterSubject));
            });
            // Perform direct gets for current subscriptions
            this.currentSubscriptions.forEach((sub, filterSubject) => {
                promises.push(this.getLatestMessagesForSubjectFilter(this.nc, this.streamName, filterSubject));
            });
            if (promises.length > 0) {
                yield Promise.all(promises);
                dbgLog(logPrefix, `Completed direct gets for ${promises.length} subscriptions`);
            }
        });
    }
    reestablishClosedSubscriptions() {
        return __awaiter(this, void 0, void 0, function* () {
            const closedSubscriptions = this.getClosedSubscriptions();
            if (closedSubscriptions.globalSubjects.length === 0 &&
                closedSubscriptions.currentSubjects.length === 0) {
                return;
            }
            yield this.establishSubscriptions(closedSubscriptions, true, 'reestablishClosedSubscriptions');
        });
    }
    performHealthCheck() {
        return __awaiter(this, void 0, void 0, function* () {
            const logPrefix = 'NatsClient :: performHealthCheck ::';
            console.log(logPrefix, 'HEALTH CHECK CALLED - isHealthCheckEnabled:', this.isHealthCheckEnabled, 'isConnected:', this.isConnected);
            if (!this.isHealthCheckEnabled || !this.isConnected) {
                console.log(logPrefix, 'HEALTH CHECK SKIPPED - returning early');
                return;
            }
            try {
                dbgLog(logPrefix, 'Starting health check');
                // Check connection health
                const connectionHealthy = yield this.performConnectionHealthCheck();
                // Check subscription health (inactive subjects)
                const subscriptionHealthy = yield this.performSubscriptionHealthCheck();
                // Check DirectGet health
                const directGetHealthy = yield this.performDirectGetHealthCheck();
                const overallHealthy = connectionHealthy && directGetHealthy;
                if (overallHealthy) {
                    this.consecutiveHealthFailures = 0;
                    // If subscriptions are closed, reestablish them
                    if (!subscriptionHealthy) {
                        yield this.reestablishClosedSubscriptions();
                    }
                    dbgLog(logPrefix, 'Health check completed successfully');
                }
                else {
                    this.consecutiveHealthFailures++;
                    dbgErr(logPrefix, `Health check failed. Consecutive failures: ${this.consecutiveHealthFailures}`);
                    if (this.consecutiveHealthFailures >= this.maxConsecutiveFailures) {
                        dbgErr(logPrefix, 'Max consecutive failures reached. Attempting reconnection...');
                        yield this.handleHealthFailure();
                    }
                }
            }
            catch (error) {
                dbgErr(logPrefix, 'Error during health check:', error);
                this.consecutiveHealthFailures++;
            }
        });
    }
    handleHealthFailure() {
        return __awaiter(this, void 0, void 0, function* () {
            const logPrefix = 'NatsClient :: handleHealthFailure ::';
            if (!this.connectionParams) {
                dbgErr(logPrefix, 'No connection parameters available for reconnection');
                return;
            }
            try {
                dbgLog(logPrefix, 'Attempting to recover from health failure');
                // Store current subscription state before disconnecting
                const currentSubscriptions = this.getCurrentSubscriptionState();
                // Close existing connection
                if (this.nc && !this.nc.isClosed()) {
                    yield this.nc.close();
                }
                // Reset connection state
                this.isConnected = false;
                this.isConnecting = false;
                this.consecutiveHealthFailures = 0;
                // Reconnect
                const reconnected = yield this.connectToNatServer(this.connectionParams.servers, this.connectionParams.jwtToken, this.connectionParams.streamName, this.connectionParams.refreshToken);
                if (reconnected) {
                    dbgLog(logPrefix, 'Successfully reconnected, restoring subscriptions');
                    // Perform direct gets and restore subscriptions
                    yield this.performDirectGetsForSubscriptions('healthFailureRecovery');
                    yield this.establishSubscriptions(currentSubscriptions, false, // Skip direct gets since we just did them
                    'healthFailureRecovery');
                    dbgLog(logPrefix, 'Successfully recovered from health failure');
                }
                else {
                    dbgErr(logPrefix, 'Failed to recover from health failure');
                }
            }
            catch (error) {
                dbgErr(logPrefix, 'Error during health failure recovery:', error);
            }
        });
    }
    startHealthCheckMonitoring() {
        const logPrefix = 'NatsClient :: startHealthCheckMonitoring ::';
        dbgLog(logPrefix, 'Method called. isHealthCheckEnabled:', this.isHealthCheckEnabled);
        if (!this.isHealthCheckEnabled) {
            dbgLog(logPrefix, 'Health check is disabled, returning early');
            return;
        }
        if (this.healthCheckInterval) {
            clearInterval(this.healthCheckInterval);
        }
        this.healthCheckInterval = setInterval(() => {
            this.performHealthCheck().catch((error) => {
                dbgErr(logPrefix, 'Error in health check interval:', error);
            });
        }, this.healthCheckFrequency);
        dbgLog(logPrefix, `Health check monitoring started with ${this.healthCheckFrequency}ms frequency`);
    }
    stopHealthCheckMonitoring() {
        if (this.healthCheckInterval) {
            clearInterval(this.healthCheckInterval);
            this.healthCheckInterval = null;
            dbgLog('NatsClient :: stopHealthCheckMonitoring :: Health check monitoring stopped');
        }
    }
    loadKeyFromLocalStorage() {
        const seed = cacheService.get(LOCAL_STORAGE_PRIVATE_KEY);
        //const seed = localStorage.getItem(LOCAL_STORAGE_PRIVATE_KEY);
        if (seed) {
            this.nkeyPair = fromSeed(Uint8Array.from(seed.split(',').map(Number)));
            dbgLog('Private key loaded from local storage/Cache service');
        }
        else {
            dbgLog('No private key found in local storage/Cache service');
        }
    }
    saveKeyToLocalStorage() {
        if (this.nkeyPair) {
            const seed = this.nkeyPair.getSeed();
            const publicKey = this.nkeyPair.getPublicKey();
            cacheService.set(LOCAL_STORAGE_PRIVATE_KEY, seed.toString());
            cacheService.set(LOCAL_STORAGE_PUBLIC_KEY, publicKey);
            // localStorage.setItem(LOCAL_STORAGE_PRIVATE_KEY, seed.toString());
            // localStorage.setItem(LOCAL_STORAGE_PUBLIC_KEY, publicKey);
            dbgLog('Key pair saved to local storage/cache service');
        }
    }
    generateNKeyPairIfNotExists() {
        this.loadKeyFromLocalStorage();
        if (!this.nkeyPair) {
            this.nkeyPair = createUser();
            dbgLog('Generated new NKeyPair:', this.nkeyPair.getPublicKey());
            this.saveKeyToLocalStorage();
        }
        else {
            dbgLog('Using existing key pair from local storage');
        }
    }
    // Check if the public key is already registered
    isRegistered() {
        return cacheService.get(LOCAL_STORAGE_IS_REGISTERED) === 'true';
        // return localStorage.getItem(LOCAL_STORAGE_IS_REGISTERED) === 'true';
    }
    // Mark public key as registered
    setRegistered() {
        cacheService.set(LOCAL_STORAGE_IS_REGISTERED, 'true');
        //localStorage.setItem(LOCAL_STORAGE_IS_REGISTERED, 'true');
    }
    // Register the public key with the server (only once)
    registerPublicKey() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isRegistered()) {
                dbgLog('registerPublicKey:: Public key already registered');
                //return true;
            }
            if (!this.nkeyPair) {
                dbgErr('registerPublicKey:: NKeyPair not generated.');
                this.generateNKeyPairIfNotExists();
            }
            const publicKey = ((_a = this.nkeyPair) === null || _a === void 0 ? void 0 : _a.getPublicKey()) || '';
            while (true) {
                try {
                    //const signageToken = '2282e93f-f782-4958-aa09-0c33e6831aa3';
                    const signageToken = new DataService().getDeviceToken();
                    const jwtResponse = yield axios.post('/v1/register', {
                        publicKey: publicKey,
                        token: signageToken,
                    });
                    this.jwtToken = jwtResponse.data.jwt;
                    dbgLog('Successfully registered public key with the server' +
                        JSON.stringify(this.jwtToken));
                    this.setRegistered();
                    return true; // Registration was successful
                }
                catch (error) {
                    dbgErr('Error registering public key, retrying in 10 seconds...', error);
                    yield new Promise((resolve) => setTimeout(resolve, 10000)); // Wait for 10 seconds before retrying
                }
            }
        });
    }
    // Register the public key with the server (only once)
    getPublicKey() {
        var _a;
        if (!this.nkeyPair) {
            dbgErr('registerPublicKey:: NKeyPair not generated.');
            this.generateNKeyPairIfNotExists();
        }
        return ((_a = this.nkeyPair) === null || _a === void 0 ? void 0 : _a.getPublicKey()) || '';
    }
    requestJWT(authToken) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.nkeyPair) {
                dbgErr('NKeyPair not generated.');
                return;
            }
            const publicKey = this.nkeyPair.getPublicKey();
            try {
                const response = yield axios.post('/api/get-jwt', {
                    publicKey,
                    authToken,
                });
                this.jwtToken = response.data.jwt;
            }
            catch (error) {
                dbgErr('Error obtaining JWT:', error);
            }
        });
    }
    // Patch: Make processMessage public for debug
    processMessage(subject, msg, source) {
        const logPrefix = 'NatsClient :: processMessage ::';
        try {
            const decodedMessage = this.codec.decode(msg.data);
            // Update last message time for health monitoring
            this.updateLastMessageTime(subject);
            dbgLog(logPrefix, `source: ${source} :: Received message on "${subject}":`, decodedMessage);
            store.dispatch(updateMessage({ subject, payload: decodedMessage }));
        }
        catch (error) {
            dbgErr(logPrefix, `source: ${source} :: Error processing messages for subject "${subject}":`, error);
        }
    }
    updateStoreSubscribe(msg) {
        var _a, _b, _c;
        const currentNatsSequence = parseInt((_b = (_a = msg.headers) === null || _a === void 0 ? void 0 : _a.get('Nats-Sequence')) !== null && _b !== void 0 ? _b : '0');
        const subject = msg.subject.replace('republish.', '');
        const lastSequenceForSubject = (_c = this.lastReceivedSequence.get(subject)) !== null && _c !== void 0 ? _c : 0;
        if (currentNatsSequence != 0 &&
            lastSequenceForSubject < currentNatsSequence) {
            this.lastReceivedSequence.set(subject, currentNatsSequence);
            // console.log(logPrefix, "Received new update, updated stores :: subject", subject, "msg:", this.codec.decode(msg.data), "lastSequenceForSubject Map", this.lastReceivedSequence)
            this.processMessage(subject, msg, 'subscribe');
        }
    }
    updateStoreDirectGet(msg) {
        var _a, _b, _c, _d, _e;
        const currentNatsSequence = parseInt((_b = (_a = msg.headers) === null || _a === void 0 ? void 0 : _a.get('Nats-Sequence')) !== null && _b !== void 0 ? _b : '0');
        const subject = (_d = (_c = msg.headers) === null || _c === void 0 ? void 0 : _c.get('Nats-Subject')) !== null && _d !== void 0 ? _d : '';
        const lastSequenceForSubject = (_e = this.lastReceivedSequence.get(subject)) !== null && _e !== void 0 ? _e : 0;
        dbgLog('updateStoreDirectGet:: currentNatsSequence :: lastSequenceForSubject :: subject', currentNatsSequence, lastSequenceForSubject, subject);
        if (currentNatsSequence != 0 &&
            lastSequenceForSubject < currentNatsSequence) {
            this.lastReceivedSequence.set(subject, currentNatsSequence);
            // console.log(logPrefix, "Received new update, updated stores :: subject", subject, "msg:", this.codec.decode(msg.data), "lastSequenceForSubject Map", this.lastReceivedSequence)
            this.processMessage(subject, msg, 'direct-get');
        }
    }
    getSubjectsWithFilter(nc, streamName, filterSubject) {
        return __awaiter(this, void 0, void 0, function* () {
            const jsm = yield nc.jetstreamManager();
            const streamInfo = yield jsm.streams.info(streamName, {
                subjects_filter: filterSubject,
            });
            const subjects = streamInfo.state.subjects;
            // const subjectsToPrint = [""]
            // for (let subject in subjects)
            //   subjectsToPrint.push(subject)
            // console.log(`NatsClient :: getSubjectsWithFilter :: ${subjectsToPrint}`)
            return subjects;
        });
    }
    getLatestMessageForSubject(nc, streamName, subject) {
        return __awaiter(this, void 0, void 0, function* () {
            const logPrefix = 'NatsClient :: getLatestMessageForSubject ::';
            try {
                const DIRECT_GET_SUBJECT_PREFIX = '$JS.API.DIRECT.GET.' + streamName + '.';
                const requestPayload = '';
                const directGetSubject = DIRECT_GET_SUBJECT_PREFIX + subject;
                const reply = yield nc.request(directGetSubject, requestPayload);
                this.updateStoreDirectGet(reply);
                // console.log(logPrefix, "recieved reply for direct get :: subject", subject);
            }
            catch (err) {
                sendAlertToServer(Constants.ALERTS.JETSTREAMS_DIRECT_GET_FAIL, JSON.stringify(subject) + ' :: ' + JSON.stringify(err));
                dbgErr(logPrefix, 'error in getting latest message using direct get for subject', subject, err);
                return null;
            }
        });
    }
    getLatestMessagesForSubjectFilter(nc, streamName, filterSubject) {
        return __awaiter(this, void 0, void 0, function* () {
            const logPrefix = 'NatsClient :: getLatestMessagesForSubjectFilter ::';
            try {
                if (filterSubject === null || nc === null || streamName === null)
                    return;
                const subjectStartTime = Date.now();
                const subjects = yield this.getSubjectsWithFilter(nc, streamName, filterSubject);
                let count = 0;
                const latestMessagePromises = new Array();
                for (const subject in subjects) {
                    const latestMessagePromise = this.getLatestMessageForSubject(nc, streamName, subject);
                    latestMessagePromises.push(latestMessagePromise);
                    count = count + 1;
                }
                yield Promise.all(latestMessagePromises);
                const latestMessageEndTime = Date.now();
                dbgLog(logPrefix, 'total time taken to get latest message for filterSubject', filterSubject, 'with', count, 'subjects:', latestMessageEndTime - subjectStartTime, 'ms');
            }
            catch (err) {
                // console.error("NatsClient :: getLatestMessagesForSubjectFilter :: Error in getting the latest message for subject filter", filterSubject, "error:", err)
            }
        });
    }
    handleConnectionStatus(nc) {
        var _a, e_1, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            const logPrefix = 'NatsClient :: handleConnectionStatus ::';
            const myConnectionId = this.connectionId;
            dbgLog(logPrefix, 'starting handleConnectionStatus handler');
            try {
                for (var _d = true, _e = __asyncValues(nc.status()), _f; _f = yield _e.next(), _a = _f.done, !_a;) {
                    _c = _f.value;
                    _d = false;
                    try {
                        const status = _c;
                        if (this.connectionId !== myConnectionId)
                            break;
                        if (status.type === Events.Reconnect ||
                            status.type === Events.Disconnect) {
                            dbgLog(logPrefix, `Client ${status.type} - ${status.data}` +
                                `\ncurrent subscriptions: ${Array.from(this.currentSubscriptions.keys()).join(',')} ` +
                                `\nglobal subscriptions: ${Array.from(this.globalSubscriptions.keys()).join(',')} `);
                            // Reset sequence tracking on every reconnect/disconnect. If the
                            // backend migrated the NATS cluster, its stream sequences restart low
                            // and would otherwise be dropped by the stale higher sequences.
                            this.clearLastReceivedSequence();
                            // Use common method for direct gets
                            yield this.performDirectGetsForSubscriptions('connectionStatusChange');
                        }
                    }
                    finally {
                        _d = true;
                    }
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (!_d && !_a && (_b = _e.return)) yield _b.call(_e);
                }
                finally { if (e_1) throw e_1.error; }
            }
        });
    }
    startSubscriber(nc, filterSubject) {
        const logPrefix = 'NatsClient :: startSubscriber ::';
        if (nc === null) {
            dbgLog(logPrefix, 'nats connection is null, not starting subscriber for filterSubject', filterSubject);
            return null;
        }
        dbgLog(logPrefix, 'starting subscriber for filter subject', filterSubject);
        const republishPrefix = 'republish.';
        const subject = republishPrefix + filterSubject;
        let count = 0;
        try {
            const sub = nc.subscribe(subject, {
                callback: (err, msg) => {
                    if (err) {
                        dbgErr(err.message);
                        return null;
                    }
                    // console.log("NatsClient :: startSubscriber :: received message on subscribe :: count:", count, "msg:", this.codec.decode(msg.data), "subject:", msg.subject,":: err:", err)
                    this.updateStoreSubscribe(msg);
                    count = count + 1;
                },
            });
            dbgLog(logPrefix, 'started subscriber on', subject);
            return sub;
        }
        catch (err) {
            sendAlertToServer(Constants.ALERTS.JETSTREAMS_SUBSCRIPTION_FAIL, subject + ' :: ' + JSON.stringify(err));
            return null;
        }
    }
    clearSubsriptions(subscriptions, type) {
        const logPrefix = 'NatsClient :: clearSubsriptions :: ' + type + ' ::';
        subscriptions.forEach((sub, subject) => {
            try {
                sub === null || sub === void 0 ? void 0 : sub.unsubscribe();
                // Clean up health monitoring data for this subject
                this.lastMessageTime.delete(subject);
                dbgLog(logPrefix, 'unsubscribed subscription for subject', subject);
            }
            catch (err) {
                dbgErr(logPrefix, 'error in unsubscribing subscription for subject', subject, err);
            }
        });
        subscriptions.clear();
        dbgLog(logPrefix, 'cleared subscriptions');
    }
    clearAllGlobalSubscriptions() {
        this.clearSubsriptions(this.globalSubscriptions, 'global');
    }
    subscribe(subjects) {
        return __awaiter(this, void 0, void 0, function* () {
            const logPrefix = 'NatsClient :: subscribe ::';
            dbgLog(logPrefix, 'adding subscription for subjects', subjects);
            try {
                this.clearSubsriptions(this.currentSubscriptions, 'current');
                for (const subject of subjects) {
                    const sub = this.startSubscriber(this.nc, subject);
                    if (!sub) {
                        dbgErr(logPrefix, 'error while starting subscription for subject', subject);
                        throw Error(`error while starting subscription for subject ${subject}`);
                    }
                    dbgLog(logPrefix, 'started subscription, added subscription to current subscriptions for subject', subject, ':: current subscriptions:', Array.from(this.currentSubscriptions.keys()).join(','));
                    this.currentSubscriptions.set(subject, sub);
                    // Initialize message time tracking for health monitoring
                    this.updateLastMessageTime(subject);
                    yield this.getLatestMessagesForSubjectFilter(this.nc, this.streamName, subject);
                }
            }
            catch (error) {
                sendAlertToServer(Constants.ALERTS.JETSTREAMS_SUBSCRIPTION_FAIL, JSON.stringify(subjects) + ' :: ' + JSON.stringify(error));
                dbgErr(logPrefix, `error subscribing to subjects "${subjects}":`, error);
            }
        });
    }
    addGlobalSubscriptions(subjects) {
        return __awaiter(this, void 0, void 0, function* () {
            const logPrefix = 'NatsClient :: subscribe :: global';
            dbgLog(logPrefix, 'adding subscription for subjects', subjects);
            try {
                for (const subject of subjects) {
                    const sub = this.startSubscriber(this.nc, subject);
                    if (!sub) {
                        dbgErr(logPrefix, 'error while starting subscription for subject', subject);
                        throw Error(`error while starting subscription for subject ${subject}`);
                    }
                    dbgLog(logPrefix, 'started subscription, adding subscription to global subscriptions for subject', subject, ':: global subscriptions:', Array.from(this.globalSubscriptions.keys()).join(','));
                    this.globalSubscriptions.set(subject, sub);
                    // Initialize message time tracking for health monitoring
                    this.updateLastMessageTime(subject);
                    yield this.getLatestMessagesForSubjectFilter(this.nc, this.streamName, subject);
                }
            }
            catch (error) {
                dbgErr(logPrefix, `error subscribing to subjects "${subjects}":`, error);
            }
        });
    }
    publishToStream(subject, message) {
        return __awaiter(this, void 0, void 0, function* () {
            const logPrefix = 'NatsClient :: publishToStream ::';
            if (!this.jetStreamClient) {
                dbgErr(logPrefix, 'JetStream client not initialized.');
                return;
            }
            const codec = JSONCodec();
            try {
                yield this.jetStreamClient.publish(subject, codec.encode(message));
                dbgLog(logPrefix, `Message published on "${subject}":`, message);
            }
            catch (error) {
                sendAlertToServer(Constants.ALERTS.JETSTREAMS_PUBLISH_FAIL, JSON.stringify(error));
                dbgErr(logPrefix, 'error publishing to stream:', error);
            }
        });
    }
    connectToNatServer(servers, jwtToken, streamName, refreshToken) {
        return __awaiter(this, void 0, void 0, function* () {
            const logPrefix = 'NatsClient :: connectToNatServer ::';
            console.log(logPrefix, 'CONNECTION ATTEMPT STARTED - servers:', servers, 'streamName:', streamName);
            if (this.isConnected) {
                dbgLog(logPrefix, 'Already connected to NATS server.');
                return true;
            }
            if (this.isConnecting) {
                dbgLog(logPrefix, 'Connection already in progress.');
                return false;
            }
            this.isConnecting = true;
            try {
                dbgLog(logPrefix, 'Trying to connect to NATS server:', servers);
                if (!jwtToken) {
                    dbgErr(logPrefix, 'JWT token is missing after requestJWT');
                    this.isConnecting = false;
                    return false;
                }
                // Store connection parameters for health check recovery
                this.connectionParams = {
                    servers,
                    jwtToken,
                    refreshToken: refreshToken || '',
                    streamName,
                };
                this.generateNKeyPairIfNotExists();
                const authenticator = jwtAuthenticator(jwtToken, () => {
                    if (!this.nkeyPair) {
                        throw new Error('NKeyPair is not available');
                    }
                    return this.nkeyPair.getSeed();
                });
                const options = {
                    servers: servers,
                    authenticator,
                    reconnect: true,
                    reconnectTimeWait: 2000, // 2 seconds reconnect wait time
                };
                this.nc = yield connect(options);
                dbgLog(logPrefix, 'Connected to NATS using JWT!');
                this.configureListeners();
                this.jetStreamClient = this.nc.jetstream();
                this.streamName = streamName;
                this.clearAllGlobalSubscriptions();
                this.handleConnectionStatus(this.nc);
                this.isConnected = true;
                this.isConnecting = false;
                // Start health check monitoring
                dbgLog(logPrefix, 'About to start health check monitoring...');
                this.startHealthCheckMonitoring();
                dbgLog(logPrefix, 'Health check monitoring start attempt completed');
                return true;
            }
            catch (error) {
                dbgErr(logPrefix, 'Failed to connect to NATS server:', error);
                this.isConnecting = false;
                sendAlertToServer(Constants.ALERTS.JETSTREAMS_CONNECT_FAIL, JSON.stringify(error));
                // Retry connection after a delay
                if (this.connectRetryTimeout)
                    clearTimeout(this.connectRetryTimeout);
                this.connectRetryTimeout = setTimeout(() => {
                    this.connectRetryTimeout = null;
                    dbgLog(logPrefix, 'Retrying connection to NATS server...');
                    this.connectToNatServer(servers, jwtToken, streamName, refreshToken);
                }, 10000); // Retry after 10 seconds
            }
            return false;
        });
    }
    // Update JWT token and reconnect
    updateJWTAndReconnect(newJwtToken, newRefreshToken) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.connectionParams) {
                console.log('NatsClient :: No connection parameters available for reconnection');
                return false;
            }
            if (!newJwtToken) {
                console.log('NatsClient :: New JWT token is missing');
                return false;
            }
            if (!newRefreshToken) {
                console.log('NatsClient :: New refresh token is missing');
                return false;
            }
            try {
                console.log('NatsClient :: Updating JWT token and reconnecting...');
                // Store current subscription state before disconnecting
                const currentSubscriptions = this.getCurrentSubscriptionState();
                // Disconnect existing connection (but preserve connection params)
                yield this.disconnect();
                // Update connection parameters with BOTH new tokens
                this.connectionParams.jwtToken = newJwtToken;
                this.connectionParams.refreshToken = newRefreshToken;
                console.log('NatsClient :: Updated connection parameters with new tokens');
                // Reconnect with new JWT token and refresh token
                const reconnected = yield this.connectToNatServer(this.connectionParams.servers, newJwtToken, this.connectionParams.streamName, newRefreshToken);
                if (reconnected) {
                    console.log('NatsClient :: Successfully reconnected with new JWT token');
                    // Restore all subscriptions with direct gets
                    yield this.establishSubscriptions(currentSubscriptions, true, // Include direct gets to get latest messages
                    'jwtTokenUpdate');
                    return true;
                }
                else {
                    console.log('NatsClient :: Failed to reconnect with new JWT token');
                    return false;
                }
            }
            catch (error) {
                console.log('NatsClient :: Error during JWT update and reconnection:', error);
                return false;
            }
        });
    }
    disconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            const logPrefix = 'NatsClient :: disconnect ::';
            try {
                // Stop health monitoring
                this.stopHealthCheckMonitoring();
                // Clear subscriptions
                this.clearSubsriptions(this.currentSubscriptions, 'current');
                this.clearAllGlobalSubscriptions();
                // Close connection
                if (this.nc && !this.nc.isClosed()) {
                    yield this.nc.close();
                }
                // Invalidate old async iterators
                this.connectionId++;
                // Clear pending retry timeout
                if (this.connectRetryTimeout) {
                    clearTimeout(this.connectRetryTimeout);
                    this.connectRetryTimeout = null;
                }
                // Reset state
                this.isConnected = false;
                this.isConnecting = false;
                this.nc = null;
                this.jetStreamClient = null;
                this.streamName = null;
                // this.connectionParams = null;
                this.lastMessageTime.clear();
                this.clearLastReceivedSequence();
                this.consecutiveHealthFailures = 0;
                // dbgLog(logPrefix, 'Successfully disconnected from NATS server');
                console.log('NatsClient :: Successfully disconnected from NATS server');
            }
            catch (error) {
                dbgErr(logPrefix, 'Error during disconnect:', error);
            }
        });
    }
    getHealthStatus() {
        const lastMessageTimes = {};
        this.lastMessageTime.forEach((time, subject) => {
            lastMessageTimes[subject] = time;
        });
        // Get detailed subscription status
        const subscriptionStatus = {
            global: {},
            current: {},
        };
        this.globalSubscriptions.forEach((subscription, subject) => {
            subscriptionStatus.global[subject] = {
                active: !this.isSubjectInactive(subject),
                closed: subscription ? subscription.isClosed() : true,
            };
        });
        this.currentSubscriptions.forEach((subscription, subject) => {
            subscriptionStatus.current[subject] = {
                active: !this.isSubjectInactive(subject),
                closed: subscription ? subscription.isClosed() : true,
            };
        });
        return {
            isConnected: this.isConnected,
            isHealthCheckEnabled: this.isHealthCheckEnabled,
            consecutiveFailures: this.consecutiveHealthFailures,
            lastMessageTimes,
            subscriptionStatus,
        };
    }
    getMessageForSubject(subject) {
        return __awaiter(this, void 0, void 0, function* () {
            const logPrefix = 'NatsClient :: getMessageForSubject ::';
            if (!this.nc) {
                dbgErr(logPrefix, 'NATS connection is not initialized');
                return null;
            }
            if (!this.streamName) {
                dbgErr(logPrefix, 'Stream name is not set');
                return null;
            }
            try {
                dbgLog(logPrefix, 'getting latest message for subject', subject);
                const directGetSubject = `$JS.API.DIRECT.GET.${this.streamName}.${subject}`;
                const reply = yield this.nc.request(directGetSubject);
                const decoded = this.codec.decode(reply.data);
                // Update health monitoring - this counts as activity for the subject
                this.updateLastMessageTime(subject);
                this.updateStoreDirectGet(reply);
                return decoded;
            }
            catch (err) {
                dbgErr(logPrefix, 'error in getting latest message using direct get for subject', subject, err);
                return null;
            }
        });
    }
    configureListeners() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.nc) {
                const myConnectionId = this.connectionId;
                (() => __awaiter(this, void 0, void 0, function* () {
                    var _a, e_2, _b, _c;
                    if (this.nc) {
                        try {
                            for (var _d = true, _e = __asyncValues(this.nc.status()), _f; _f = yield _e.next(), _a = _f.done, !_a;) {
                                _c = _f.value;
                                _d = false;
                                try {
                                    const s = _c;
                                    if (this.connectionId !== myConnectionId)
                                        break;
                                    switch (s.type) {
                                        case 'error':
                                            sendAlertToServer(Constants.ALERTS.JETSTREAMS_ERROR, JSON.stringify(s.data));
                                            break;
                                        case 'disconnect':
                                            // sendAlertToServer(
                                            //   Constants.ALERTS.JETSTREAMS_DISCONNECT,
                                            //   JSON.stringify(s.data)
                                            // );
                                            break;
                                        case 'reconnect':
                                            break;
                                        default:
                                    }
                                }
                                finally {
                                    _d = true;
                                }
                            }
                        }
                        catch (e_2_1) { e_2 = { error: e_2_1 }; }
                        finally {
                            try {
                                if (!_d && !_a && (_b = _e.return)) yield _b.call(_e);
                            }
                            finally { if (e_2) throw e_2.error; }
                        }
                    }
                }))();
            }
        });
    }
}
// Initialize global health check debugging functions
const natsInstance = NatsClient.getInstance();
window.__NATS_HEALTH_STATUS = () => natsInstance.getHealthStatus();
window.__NATS_SET_INACTIVITY_TIMEOUT = (timeout) => natsInstance.setInactivityTimeout(timeout);
window.__NATS_SET_HEALTH_FREQUENCY = (frequency) => natsInstance.setHealthCheckFrequency(frequency);
window.__NATS_ENABLE_HEALTH_CHECK = (enabled) => natsInstance.enableHealthCheck(enabled);
export default NatsClient.getInstance();
